package info.rezident.app.pages

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import info.rezident.app.seo.*
import info.rezident.app.ui.theme.AppTheme
import info.rezident.app.ui.theme.AppTypography
import kotlinx.browser.document
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.*

@Page("confidentialitate")
@Composable
fun PrivacyPolicyPage() {
    val title = "Politica de confidențialitate - rezident.info"
    val description =
        "Află ce date colectăm, temeiul legal (GDPR), durata de stocare și drepturile tale. Ultima actualizare: 15 noiembrie 2025."
    val canonicalUrl = "https://rezident.info/confidentialitate"
    val ogImageUrl = "https://rezident.info/assets/og/og-image.png"

    Seo(title = title, description = description)

    LaunchedEffect(Unit) {
        document.setCanonicalUrl(canonicalUrl)
        document.setMetaNameTag(name = "robots", content = "index, follow")
        document.setMetaNameTag(name = "twitter:card", content = "summary_large_image")
        document.setMetaNameTag(name = "twitter:title", content = title)
        document.setMetaNameTag(name = "twitter:description", content = description)
        document.setMetaNameTag(name = "twitter:image", content = ogImageUrl)

        document.setOpenGraphTag(property = "og:title", content = title)
        document.setOpenGraphTag(property = "og:description", content = description)
        document.setOpenGraphTag(property = "og:url", content = canonicalUrl)
        document.setOpenGraphTag(property = "og:image", content = ogImageUrl)
    }

    val privacyJsonLd: String = """
        {
          "@context": "https://schema.org",
          "@type": "WebPage",
          "url": "https://rezident.info/confidentialitate",
          "name": "Politica de Confidențialitate - rezident.info",
          "inLanguage": "ro-RO",
          "dateModified": "2026-04-02",
          "isPartOf": {
            "@id": "https://rezident.info/#website"
          }
        }
    """.trimIndent()

    ApplicationLdJson(privacyJsonLd)

    Column(
        modifier = Modifier.fillMaxWidth().padding(32.px).gap(16.px).backgroundColor(AppTheme.colors.background),
        horizontalAlignment = Alignment.Start,
        verticalArrangement = Arrangement.spacedBy(16.px)
    ) {
        SpanText("Politica de Confidențialitate", modifier = AppTypography.h1.toModifier())
        SpanText("Platformă: rezident.info", modifier = AppTypography.body1.toModifier())
        SpanText("Ultima actualizare: 2 aprilie 2026", modifier = AppTypography.body1.toModifier())

        SpanText("1. Cine suntem", modifier = AppTypography.h2.toModifier())
        SpanText(
            "Operatorul de date este MEȘINĂ ANDREI PERSOANĂ FIZICĂ AUTORIZATĂ, care operează platforma rezident.info.",
            modifier = AppTypography.body1.toModifier()
        )
        SpanText("CUI: 54277907", modifier = AppTypography.body1.toModifier())
        SpanText("Nr. Reg. Comerțului: F2026014551006", modifier = AppTypography.body1.toModifier())
        SpanText(
            "Adresă: B-dul Bucureștii Noi, 136, et. Parter, ap. 5, Sector 1, București",
            modifier = AppTypography.body1.toModifier()
        )
        SpanText("Contact: salut@rezident.info.", modifier = AppTypography.body1.toModifier())

        SpanText("2. Datele pe care le colectăm", modifier = AppTypography.h2.toModifier())
        Table(attrs = Modifier.fillMaxWidth().toAttrs()) {
            Thead {
                Tr {
                    Th(
                        attrs = AppTypography.body1.toModifier().textAlign(TextAlign.Left).fontWeight(FontWeight.Bold)
                            .toAttrs()
                    ) { SpanText("Categorie", modifier = AppTypography.body1.toModifier().fontWeight(FontWeight.Bold)) }
                    Th(
                        attrs = AppTypography.body1.toModifier().textAlign(TextAlign.Left).fontWeight(FontWeight.Bold)
                            .toAttrs()
                    ) { SpanText("Exemple", modifier = AppTypography.body1.toModifier().fontWeight(FontWeight.Bold)) }
                    Th(
                        attrs = AppTypography.body1.toModifier().textAlign(TextAlign.Left).fontWeight(FontWeight.Bold)
                            .toAttrs()
                    ) {
                        SpanText(
                            "Baza legală (GDPR)",
                            modifier = AppTypography.body1.toModifier().fontWeight(FontWeight.Bold)
                        )
                    }
                }
            }
            Tbody {
                Tr {
                    Td { SpanText("Date de cont", modifier = AppTypography.body1.toModifier()) }
                    Td {
                        SpanText(
                            "nume, e-mail, autentificare (Google / e-mail)",
                            modifier = AppTypography.body1.toModifier()
                        )
                    }
                    Td {
                        SpanText(
                            "art. 6 (1) b – executarea contractului",
                            modifier = AppTypography.body1.toModifier()
                        )
                    }
                }
                Tr {
                    Td { SpanText("Răspunsuri la Quiz", modifier = AppTypography.body1.toModifier()) }
                    Td {
                        SpanText(
                            "preferințe, aptitudini, obiective; status acces (blocări/deblocări)",
                            modifier = AppTypography.body1.toModifier()
                        )
                    }
                    Td { SpanText("art. 6 (1) b", modifier = AppTypography.body1.toModifier()) }
                }
                Tr {
                    Td { SpanText("Feedback", modifier = AppTypography.body1.toModifier()) }
                    Td {
                        SpanText(
                            "rating-uri (emoji), comentarii privind recomandările",
                            modifier = AppTypography.body1.toModifier()
                        )
                    }
                    Td {
                        SpanText(
                            "art. 6 (1) f – interes legitim (îmbunătățirea serviciului)",
                            modifier = AppTypography.body1.toModifier()
                        )
                    }
                }
                Tr {
                    Td { SpanText("Plăți Stripe", modifier = AppTypography.body1.toModifier()) }
                    Td {
                        SpanText(
                            "ID sesiune checkout, preț selectat, status tranzacție (fără date card)",
                            modifier = AppTypography.body1.toModifier()
                        )
                    }
                    Td { SpanText("art. 6 (1) b", modifier = AppTypography.body1.toModifier()) }
                }
                Tr {
                    Td { SpanText("Date tehnice", modifier = AppTypography.body1.toModifier()) }
                    Td {
                        SpanText(
                            "IP, device, browser, cookies, evenimente Firebase Analytics / performanță",
                            modifier = AppTypography.body1.toModifier()
                        )
                    }
                    Td {
                        SpanText(
                            "art. 6 (1) f – interes legitim (securitate & analytics)",
                            modifier = AppTypography.body1.toModifier()
                        )
                    }
                }
                Tr {
                    Td { SpanText("Marketing opțional", modifier = AppTypography.body1.toModifier()) }
                    Td { SpanText("preferințe e-mail", modifier = AppTypography.body1.toModifier()) }
                    Td { SpanText("art. 6 (1) a – consimțământ", modifier = AppTypography.body1.toModifier()) }
                }
            }
        }
        SpanText(
            "Nu colectăm categorii speciale de date (sănătate, etnie etc.).",
            modifier = AppTypography.body1.toModifier()
        )
        SpanText(
            "Nu stocăm și nu procesăm date complete de card; acestea sunt gestionate exclusiv de Stripe.",
            modifier = AppTypography.body1.toModifier()
        )

        SpanText("3. Cum folosim datele", modifier = AppTypography.h2.toModifier())
        SpanText(
            "• Furnizarea serviciului (creare cont, generare Recomandări și gestionarea accesului).",
            modifier = AppTypography.body1.toModifier()
        )
        SpanText("• Analiză produs (metrici de utilizare, A/B-testing).", modifier = AppTypography.body1.toModifier())
        SpanText(
            "• Comunicări tranzacționale și marketing (cu opțiune de dezabonare).",
            modifier = AppTypography.body1.toModifier()
        )
        SpanText("• Prevenirea fraudelor și securitate.", modifier = AppTypography.body1.toModifier())
        SpanText(
            "• Îmbunătățirea algoritmului și a explicațiilor generate de AI (fără a expune public răspunsuri individuale).",
            modifier = AppTypography.body1.toModifier()
        )

        SpanText("4. Destinatari", modifier = AppTypography.h2.toModifier())
        SpanText("• Stripe (procesare plăți, checkout).", modifier = AppTypography.body1.toModifier())
        SpanText("• Firebase (hosting, autentificare, analytics).", modifier = AppTypography.body1.toModifier())
        SpanText(
            "• Furnizori cloud din UE/SEE sau SUA, protejați prin Clauze Contractuale Standard (SCC).",
            modifier = AppTypography.body1.toModifier()
        )
        SpanText("Nu vindem datele tale către terți.", modifier = AppTypography.body1.toModifier())
        SpanText(
            "Serviciile terțe au propriile politici de confidențialitate și termeni; utilizarea lor este supusă acelor documente.",
            modifier = AppTypography.body1.toModifier()
        )

        SpanText("5. Transferuri internaționale", modifier = AppTypography.h2.toModifier())
        SpanText(
            "Dacă serverele furnizorilor se află în afara SEE, ne bazăm pe SCC și măsuri suplimentare de securitate.",
            modifier = AppTypography.body1.toModifier()
        )

        SpanText("6. Păstrarea datelor", modifier = AppTypography.h2.toModifier())
        SpanText(
            "• Date de cont: pe durata existenței contului + 3 ani după închidere (obligații fiscale).",
            modifier = AppTypography.body1.toModifier()
        )
        SpanText(
            "• Răspunsuri la Quiz și recomandări asociate: până la ștergerea contului sau solicitarea de ștergere.",
            modifier = AppTypography.body1.toModifier()
        )
        SpanText("• Date de analiză agregate: anonimizate după 24 luni.", modifier = AppTypography.body1.toModifier())

        SpanText("7. Drepturile tale", modifier = AppTypography.h2.toModifier())
        SpanText("• Acces, rectificare, ștergere.", modifier = AppTypography.body1.toModifier())
        SpanText("• Restricționare sau opoziție la prelucrare.", modifier = AppTypography.body1.toModifier())
        SpanText("• Portabilitatea datelor.", modifier = AppTypography.body1.toModifier())
        SpanText(
            "• Retragerea consimțământului oricând, fără a afecta legalitatea prelucrării anterioare.",
            modifier = AppTypography.body1.toModifier()
        )
        SpanText("• Plângere la ANSPDCP (www.dataprotection.ro).", modifier = AppTypography.body1.toModifier())
        SpanText(
            "Solicitările se trimit la salut@rezident.info și vor fi procesate în max. 30 zile.",
            modifier = AppTypography.body1.toModifier()
        )

        SpanText("8. Securitate", modifier = AppTypography.h2.toModifier())
        SpanText(
            "Aplicăm criptare TLS, stocare hashed pentru parole, backup zilnic și monitorizare continuă a accesului.",
            modifier = AppTypography.body1.toModifier()
        )

        SpanText("9. Cookie-uri", modifier = AppTypography.h2.toModifier())
        SpanText(
            "Folosim cookie-uri necesare (autentificare) și analitice (Firebase/GA4).",
            modifier = AppTypography.body1.toModifier()
        )

        SpanText("10. Modificări ale politicii", modifier = AppTypography.h2.toModifier())
        SpanText(
            "Publicăm versiunile actualizate pe această pagină și notificăm utilizatorii autentificați cu cel puțin 14 zile înainte de aplicare dacă schimbările sunt semnificative.",
            modifier = AppTypography.body1.toModifier()
        )

        SpanText("Întrebări?", modifier = AppTypography.h2.toModifier())
        SpanText(
            "Scrie-ne la salut@rezident.info.",
            modifier = AppTypography.body1.toModifier()
        )
    }
}